import React, { createContext, useEffect, useRef, useState } from 'react';
import firebase, { db } from '../utils/firebase';
import { InvitationProblem, invitationProblemFor, RequestStatus, requestStatusFor } from '../utils/requestStatus';

export enum UserRoles {
  WAITER = 'waiter',
  CHEFWAITER = 'chefWaiter',
  TABLET = 'tablet',
  ADMIN = 'admin',
}

export type Products =
  | 'general'
  | 'delivery'
  | 'vouchers'
  | 'merchandise'
  | 'followUp'
  | 'menu'
  | 'feedback'
  | 'reservation';

export type Restaurant = {
  id: string;
  name?: string;
  admin: boolean;
  role?: string;
  title?: string;
  products: Products[];
};

export type Invitation = {
  // The invitation link this state belongs to.
  id: string | null;
  loading: boolean;
  invalid: boolean;
  restaurantId: string | null;
  restaurantName: string | null;
};

// Where the person goes after a sign-in or sign-up: the status of their request,
// or a name prompt when no name is known for the request yet.
export type JoinResult = { next: 'status' | 'name' } | { problem: InvitationProblem };

export type JoinMode = 'signUp' | 'signIn';

export type AuthContextType = {
  db: firebase.firestore.Firestore;
  invitation: Invitation;
  // False until Firebase restored or ruled out a previous session.
  authReady: boolean;
  user: firebase.User | null;
  // A sign-in or sign-up of this page is still sending the request.
  joining: boolean;
  init: (invitationId: string) => Promise<void>;
  joinWithEmail: (form: { mode: JoinMode; email: string; password: string; name?: string }) => Promise<JoinResult>;
  joinWithGoogle: () => Promise<JoinResult>;
  sendRequest: (name: string) => Promise<{ problem?: InvitationProblem }>;
  requestStatus: (uid: string, restaurantId: string) => Promise<RequestStatus>;
  signOutUser: () => Promise<void>;
};

const noInvitation: Invitation = { id: null, loading: true, invalid: false, restaurantId: null, restaurantName: null };

// A route can switch to another invitation link: only its own, finished state counts.
export const invitationLoadedFor = (invitation: Invitation, invitationId: string) =>
  invitation.id === invitationId && !invitation.loading;

export const AuthContext = createContext<AuthContextType>({
  db,
  invitation: noInvitation,
  authReady: false,
  user: null,
  joining: false,
  init: async () => {},
  joinWithEmail: async () => ({ problem: 'unknown' }),
  joinWithGoogle: async () => ({ problem: 'unknown' }),
  sendRequest: async () => ({}),
  requestStatus: async () => 'missing',
  signOutUser: async () => {},
});

export const requestRefs = (uid: string, restaurantId: string) => ({
  profile: db.collection('users').doc(uid),
  membership: db.collection(`restaurants/${restaurantId}/users`).doc(uid),
  request: db.collection(`restaurants/${restaurantId}/pendingInvitations`).doc(uid),
});

const AuthContextProvider = ({ children }: any) => {
  const [invitation, setinvitation] = useState<Invitation>(noInvitation);
  const [authReady, setauthReady] = useState(false);
  const [user, setuser] = useState<firebase.User | null>(null);
  const [joining, setjoining] = useState(false);
  const latestInit = useRef(0);

  useEffect(
    () =>
      firebase.auth().onAuthStateChanged((currentUser) => {
        setuser(currentUser);
        setauthReady(true);
      }),
    []
  );

  const init = async (invitationId: string) => {
    const call = ++latestInit.current;
    setinvitation({ ...noInvitation, id: invitationId });
    try {
      const doc = await db.collection('waiterInvitation').doc(invitationId).get();
      if (call !== latestInit.current) return;
      setinvitation({
        id: invitationId,
        loading: false,
        invalid: !doc.exists,
        restaurantId: doc.data()?.restaurantId ?? null,
        restaurantName: doc.data()?.restaurantName ?? null,
      });
    } catch (error) {
      console.error(error);
      if (call !== latestInit.current) return;
      setinvitation({ ...noInvitation, id: invitationId, loading: false, invalid: true });
    }
  };

  const requestStatus = async (uid: string, restaurantId: string) => {
    const refs = requestRefs(uid, restaurantId);
    const [profile, membership, request] = await Promise.all([
      refs.profile.get(),
      refs.membership.get(),
      refs.request.get(),
    ]);
    return requestStatusFor({
      membership: membership.exists,
      profileHasRestaurant: !!profile.data()?.restaurants?.some((r: Restaurant) => r.id === restaurantId),
      request: request.exists ? request.data() : null,
    });
  };

  const writeRequest = (uid: string, restaurantId: string, name: string) =>
    requestRefs(uid, restaurantId).request.set({ name, status: 'pending', createdAt: Date.now() });

  // An existing request or access is reused, never sent a second time.
  const continueAs = async (signedIn: firebase.User, name?: string): Promise<JoinResult> => {
    const { restaurantId } = invitation;
    if (!restaurantId) return { problem: 'invalid-link' };

    if ((await requestStatus(signedIn.uid, restaurantId)) !== 'missing') return { next: 'status' };

    const requestName = name?.trim() || signedIn.displayName?.trim();
    if (!requestName) return { next: 'name' };

    await writeRequest(signedIn.uid, restaurantId, requestName);
    return { next: 'status' };
  };

  // The account exists before the request does: keep the page until both are done.
  const whileJoining = async (join: () => Promise<JoinResult>) => {
    setjoining(true);
    try {
      return await join();
    } finally {
      setjoining(false);
    }
  };

  const joinWithEmail: AuthContextType['joinWithEmail'] = ({ mode, email, password, name }) =>
    whileJoining(async () => {
      const auth = firebase.auth();
      try {
        let signedIn: firebase.User | null;
        if (mode === 'signUp') {
          try {
            signedIn = (await auth.createUserWithEmailAndPassword(email, password)).user;
            await signedIn?.updateProfile({ displayName: name?.trim() || null });
          } catch (error: any) {
            if (error?.code !== 'auth/email-already-in-use') throw error;
            // An earlier attempt may have created the account and then failed to
            // send the request: continue with it instead of registering again.
            try {
              signedIn = (await auth.signInWithEmailAndPassword(email, password)).user;
            } catch {
              return { problem: 'account-exists' };
            }
          }
        } else {
          signedIn = (await auth.signInWithEmailAndPassword(email, password)).user;
        }
        if (!signedIn) return { problem: 'unknown' };
        return await continueAs(signedIn, name);
      } catch (error) {
        console.error(error);
        return { problem: invitationProblemFor(error) };
      }
    });

  const joinWithGoogle = (): Promise<JoinResult> =>
    whileJoining(async () => {
      try {
        const provider = new firebase.auth.GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        const { user: signedIn } = await firebase.auth().signInWithPopup(provider);
        if (!signedIn) return { problem: 'unknown' };
        return await continueAs(signedIn);
      } catch (error) {
        console.error(error);
        return { problem: invitationProblemFor(error) };
      }
    });

  const sendRequest = async (name: string) => {
    const { restaurantId } = invitation;
    const uid = firebase.auth().currentUser?.uid;
    if (!uid || !restaurantId) return { problem: 'unknown' as const };
    try {
      await writeRequest(uid, restaurantId, name.trim());
      return {};
    } catch (error) {
      console.error(error);
      return { problem: invitationProblemFor(error) };
    }
  };

  const signOutUser = () => firebase.auth().signOut();

  return (
    <AuthContext.Provider
      value={{
        db,
        invitation,
        authReady,
        user,
        joining,
        init,
        joinWithEmail,
        joinWithGoogle,
        sendRequest,
        requestStatus,
        signOutUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
